import { WebMercatorViewport } from "@deck.gl/core"
import { calculateTrigonmetricDistance } from "@nova/calculate-geospatial"
import { DistanceUnit } from "@nova/constants"
import { useTranslation } from "@nova/use-translation"
import { formatNumberWithCommas } from "@nova/util-strings"
import { useEffect, useRef, useState } from "react"

export const ScaleBar = ({
    view,
    u,
    height,
    color,
    fontSize,
}: {
    view: WebMercatorViewport
    u: DistanceUnit
    height: number
    color?: string
    fontSize?: number
}) => {
    const ref = useRef<HTMLDivElement | null>(null)
    const { t } = useTranslation()
    const [ratio, setRatio] = useState(100)
    const [distance, setDistance] = useState(100)
    const [unit, setUnit] = useState("ft")

    useEffect(() => {
        // let scale transformation apply to element before calculating scale
        setTimeout(() => {
            if (!ref.current) return null

            const maxWidth = ref.current.getBoundingClientRect().width * 0.6
            const y = ref.current.getBoundingClientRect().top
            const x = ref.current.getBoundingClientRect().left
            const left = view.unproject([x, y])
            const right = view.unproject([x + maxWidth, y])
            const maxMeters = calculateTrigonmetricDistance(
                { latitude: left[1], longitude: left[0] },
                { latitude: right[1], longitude: right[0] }
            )
            let unit = "ft"
            let distance = maxMeters
            if (u === DistanceUnit.Feet) {
                distance = 3.2808 * distance
                if (distance > 5280) {
                    distance = distance / 5280
                    unit = "mi"
                }
            } else if (maxMeters >= 1000) {
                distance /= 1000
                unit = "km"
            } else {
                unit = "m"
            }

            function getDecimalRoundNum(d: number) {
                const multiplier = Math.pow(10, Math.ceil(-Math.log(d) / Math.LN10))
                return Math.round(d * multiplier) / multiplier
            }

            function getRoundNum(num: number) {
                const pow10 = Math.pow(10, `${Math.floor(num)}`.length - 1)
                let d = num / pow10

                d =
                    d >= 10
                        ? 10
                        : d >= 5
                          ? 5
                          : d >= 3
                            ? 3
                            : d >= 2
                              ? 2
                              : d >= 1
                                ? 1
                                : getDecimalRoundNum(d)

                return pow10 * d
            }
            const d = getRoundNum(distance)
            const ratio = getRoundNum(distance) / distance
            setRatio(ratio)
            setDistance(d)
            setUnit(unit)
        }, 0)
    }, [view, unit, ref, height, u])

    const borderColor = color ? color : "#000000"
    const textFontSize = fontSize ? fontSize : 10

    return (
        <div ref={ref} className="w-full">
            <div className="flex">
                <div className="w-3/5">
                    <div
                        className={`border-x border-b h-full flex ml-auto`}
                        style={{ width: `${ratio * 100}%`, borderColor: borderColor }}
                    >
                        <div
                            className={`w-[2px] h-1/2 mx-auto border-l mt-auto`}
                            style={{ borderColor: borderColor }}
                        />
                    </div>
                </div>
                <div
                    className={`w-2/5 flex items-center`}
                    style={{ fontSize: `${textFontSize}px` }}
                    data-tid="zoom-level"
                >
                    <div className="my-auto pl-2" style={{ color: borderColor }}>
                        {t(`${formatNumberWithCommas(distance)} ${unit}`)}
                    </div>
                </div>
            </div>
        </div>
    )
}
